/**
 * WebGPU path-tracer engine. Owns the device, pipelines, buffers, and textures.
 *
 * Lifecycle:
 *   const engine = await PathTracerEngine.create(canvas);
 *   engine.updateScene(extracted);          // uploads mesh + BVH buffers
 *   engine.updateLamps(lamps, grids);       // uploads lamps + IES texture array
 *   engine.setMaxSamples(512);
 *   // each frame:
 *   engine.render(camera, invalidate, { exposure, maxSamples });
 *   // on unmount:
 *   engine.dispose();
 */
import * as THREE from 'three';

import { buildBvh } from './bvh.ts';
import { IesGrid, LampPathtracerInput, PackedIesData, packIesData, packLampBuffer } from './iesTexture.ts';
import type { ExtractedScene, SceneMaterial } from './sceneExtract.ts';
import pathtraceWgsl from './shaders/pathtrace.wgsl?raw';
import presentWgsl from './shaders/present.wgsl?raw';

// Keep in sync with WGSL structs.
// mat4x4 (64) + vec3+pad (16) + 12 u32s (48) + vec3+f32 (16) + vec3+pad (16)
// = 160 bytes
const PARAMS_UBO_SIZE = 160;
const PRESENT_UBO_SIZE = 16; // 4 u32s

/**
 * Peak candela value that, when present as the brightest sample in the IES
 * grid, produces natural midtones at exposure = 0 EV after ACES tonemapping.
 * Empirically tuned - see writePresentUbo for how it's combined with the
 * user-facing exposure.
 */
const AUTO_EXPOSURE_REFERENCE_CD = 180;

// Material struct layout (std430, vec4 alignment = 16):
//   vec3 baseColor    (12) + f32 metallic   (4)  = 16  (offset 0)
//   vec3 emissive     (12) + f32 roughness  (4)  = 16  (offset 16)
//   i32  baseColorLayer (4) + i32 mrLayer   (4)
//     + 2 u32 pad                          (8)   = 16  (offset 32)
//   vec4 baseColorUvRow0                        = 16  (offset 48)  m11,m12,m13,_
//   vec4 baseColorUvRow1                        = 16  (offset 64)  m21,m22,m23,_
//   vec4 mrUvRow0                               = 16  (offset 80)
//   vec4 mrUvRow1                               = 16  (offset 96)
//   u32  baseColorUvChannel + u32 mrUvChannel
//     + 2 u32 pad                               = 16  (offset 112)
// Total: 128 bytes / 32 floats.
const MATERIAL_STRIDE_FLOATS = 32;
// Interleaved vertex: (px,py,pz) (nx,ny,nz) (u0,v0) (u1,v1). Both UV sets are
// always written: glTF textures reference one or the other per-texture-info,
// and without the second set certain materials collapse to all-(0,0) sampling.
const VERTEX_STRIDE_FLOATS = 10;

const DEFAULT_MAX_BOUNCES = 3;

/**
 * TS 5.7+ narrows `new Uint32Array(...)` to `Uint32Array<ArrayBufferLike>`
 * which doesn't satisfy WebGPU's `ArrayBufferView<ArrayBuffer>` constraint.
 * This helper presents a compatible view without copying.
 */
function asBufferSource(arr: ArrayBufferView): ArrayBufferView<ArrayBuffer> {
  return arr as unknown as ArrayBufferView<ArrayBuffer>;
}

export interface CameraSnapshot {
  position: THREE.Vector3;
  projectionMatrix: THREE.Matrix4;
  matrixWorldInverse: THREE.Matrix4;
}

/** Round matrix entries so float jitter (or rAF / useFrame ordering noise) does not flip the key every frame. */
function cameraKeyComponent(n: number): string {
  return String(Math.round(n * 1e5) / 1e5);
}

/** Same keying as the path-tracer `render` path — for skipping GPU work when the view is in motion. */
export function pathTracerCameraKey(cam: CameraSnapshot): string {
  const m = cam.matrixWorldInverse.elements;
  const p = cam.projectionMatrix.elements;
  return [m[0], m[5], m[12], m[13], m[14], p[0], p[5]].map(cameraKeyComponent).join(',');
}

interface RenderOptions {
  exposure: number; // EV
  maxSamples: number; // cap on accumulated samples
}

export class PathTracerEngine {
  private device: GPUDevice;
  private context: GPUCanvasContext;
  private canvas: HTMLCanvasElement;
  private canvasFormat: GPUTextureFormat;

  private renderWidth = 0;
  private renderHeight = 0;

  // Compute pipeline (path tracer)
  private ptPipeline: GPUComputePipeline | null = null;
  private ptBindGroupLayout: GPUBindGroupLayout | null = null;

  // Present pipeline
  private presentPipeline: GPURenderPipeline | null = null;
  private presentBindGroupLayout: GPUBindGroupLayout | null = null;

  // Persistent buffers
  private paramsBuffer: GPUBuffer;
  private presentParamsBuffer: GPUBuffer;

  // Scene buffers (recreated on scene change)
  private nodesBuffer: GPUBuffer | null = null;
  private indicesBuffer: GPUBuffer | null = null;
  private vertexBuffer: GPUBuffer | null = null;
  private triMatIdsBuffer: GPUBuffer | null = null;
  private materialsBuffer: GPUBuffer | null = null;

  // Lamp buffers
  private lampsBuffer: GPUBuffer | null = null;
  private lampCount = 0;

  // IES texture
  private iesTexture: GPUTexture | null = null;
  private iesLayerCount = 0;
  private iesAzSize = 128;
  private iesElSize = 64;

  // PBR material textures (baseColor + packed metallicRoughness).
  // Each is a 2D array with a canonical per-layer size and uses a shared
  // linear+repeat sampler. We keep a 1x1x1 dummy texture bound whenever a
  // scene has no textures of that kind so the bind group is always valid.
  private baseColorTexture: GPUTexture | null = null;
  private mrTexture: GPUTexture | null = null;
  /** Current canonical side length of the material texture arrays; used by
   *  the shader to convert hit footprint to a mip level. Defaults to 1 so
   *  the dummy 1x1 textures report a correct "size" before upload. */
  private materialTexSize = 1;
  private texSampler: GPUSampler;

  // Accumulation
  private accumBuffer: GPUBuffer | null = null;

  // Bind groups (recreated when any bound resource changes)
  private ptBindGroup: GPUBindGroup | null = null;
  private presentBindGroup: GPUBindGroup | null = null;

  // Node count for uniform
  private nodeCount = 0;

  // Scene readiness
  private sceneReady = false;
  private lampsReady = false;

  // Accumulation state
  private frameIndex = 0;
  /** When set, the next full path-trace `render` must pass reset into the shader (clear GPU accum). */
  private accumulationResetPending = false;
  private lastCameraKey: string | null = null;
  private sceneVersionKey = 0;

  // Baseline exposure offset (EV) derived from lamp peak candela. Applied in
  // present pass on top of the user-visible exposure slider so that "0 EV" on
  // the UI corresponds to natural midtones rather than raw cd m^-2 scaling.
  private autoExposureOffsetEv = 0;

  // Luminance-normalized tint applied to every NEE contribution. Sourced from
  // a color temperature knob on the UI; defaults to neutral white.
  private lightTint: [number, number, number] = [1, 1, 1];

  // Signed texture LOD bias (mip levels). Pinned at -4 (heavily biased toward
  // sharper mips) because our isotropic ray-cone footprint massively
  // overestimates LOD vs hardware anisotropic filtering on tiled surfaces, and
  // -4 is empirically the point where textures read correctly without
  // accumulating to mean-color. Not exposed in the UI - the old knob was
  // confusing and the right value is scene-independent in practice.
  private lodBias = -4;

  // Scalar ambient fill added on ray miss. Its chromaticity is controlled by
  // `ambientTint` (separate from `lightTint`). Default is intentionally small;
  // it's meant as a subtle lift for corners that would otherwise be pitch black.
  private ambient = 0.15;
  // Chromaticity for ambient fill (separate from lamp light tint), so ambient
  // can stay fixed regardless of the color-temperature slider.
  private ambientTint: [number, number, number] = [1, 1, 1];

  private maxBounces = DEFAULT_MAX_BOUNCES;

  private constructor(_adapter: GPUAdapter, device: GPUDevice, canvas: HTMLCanvasElement) {
    this.device = device;
    this.canvas = canvas;
    this.canvasFormat = navigator.gpu.getPreferredCanvasFormat();
    this.context = canvas.getContext('webgpu') as GPUCanvasContext;
    this.context.configure({
      device,
      format: this.canvasFormat,
      alphaMode: 'premultiplied',
    });

    this.paramsBuffer = device.createBuffer({
      size: PARAMS_UBO_SIZE,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.presentParamsBuffer = device.createBuffer({
      size: PRESENT_UBO_SIZE,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.texSampler = device.createSampler({
      magFilter: 'linear',
      minFilter: 'linear',
      mipmapFilter: 'linear',
      addressModeU: 'repeat',
      addressModeV: 'repeat',
      label: 'pbr-texture-sampler',
    });
    // Always have a valid dummy bound so updateScene can skip recreating the
    // texture array when the incoming scene has zero textures.
    this.baseColorTexture = this.createDummyTextureArray('rgba8unorm-srgb', 'dummy-basecolor');
    this.mrTexture = this.createDummyTextureArray('rgba8unorm', 'dummy-mr');

    this.createPipelines();
  }

  private createDummyTextureArray(format: GPUTextureFormat, label: string): GPUTexture {
    const tex = this.device.createTexture({
      size: { width: 1, height: 1, depthOrArrayLayers: 1 },
      format,
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      dimension: '2d',
      label,
    });
    // Single opaque-white texel so any accidental sample returns neutral.
    const neutral = new Uint8Array([255, 255, 255, 255]);
    this.device.queue.writeTexture(
      { texture: tex, origin: { x: 0, y: 0, z: 0 } },
      asBufferSource(neutral),
      { bytesPerRow: 4, rowsPerImage: 1 },
      { width: 1, height: 1, depthOrArrayLayers: 1 },
    );
    return tex;
  }

  static async create(canvas: HTMLCanvasElement): Promise<PathTracerEngine> {
    if (!navigator.gpu) throw new Error('WebGPU not available');
    const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
    if (!adapter) throw new Error('No GPU adapter available');
    const device = await adapter.requestDevice({
      requiredLimits: {
        maxStorageBufferBindingSize: Math.min(adapter.limits.maxStorageBufferBindingSize, 1 << 30),
      },
    });
    device.lost.then((info) => {
      console.error('WebGPU device lost:', info.reason, info.message);
    });
    return new PathTracerEngine(adapter, device, canvas);
  }

  private createPipelines(): void {
    const ptModule = this.device.createShaderModule({ code: pathtraceWgsl });
    const presentModule = this.device.createShaderModule({ code: presentWgsl });

    this.ptBindGroupLayout = this.device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 6, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        {
          binding: 7,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: 'unfilterable-float', viewDimension: '2d-array' },
        },
        { binding: 8, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        {
          binding: 9,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: 'float', viewDimension: '2d-array' },
        },
        {
          binding: 10,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: 'float', viewDimension: '2d-array' },
        },
        {
          binding: 11,
          visibility: GPUShaderStage.COMPUTE,
          sampler: { type: 'filtering' },
        },
      ],
    });

    this.ptPipeline = this.device.createComputePipeline({
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [this.ptBindGroupLayout] }),
      compute: { module: ptModule, entryPoint: 'cs_main' },
    });

    this.presentBindGroupLayout = this.device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: 'read-only-storage' },
        },
      ],
    });

    this.presentPipeline = this.device.createRenderPipeline({
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [this.presentBindGroupLayout] }),
      vertex: { module: presentModule, entryPoint: 'vs_main' },
      fragment: {
        module: presentModule,
        entryPoint: 'fs_main',
        targets: [{ format: this.canvasFormat }],
      },
      primitive: { topology: 'triangle-list' },
    });
  }

  dispose(): void {
    this.nodesBuffer?.destroy();
    this.indicesBuffer?.destroy();
    this.vertexBuffer?.destroy();
    this.triMatIdsBuffer?.destroy();
    this.materialsBuffer?.destroy();
    this.lampsBuffer?.destroy();
    this.iesTexture?.destroy();
    this.baseColorTexture?.destroy();
    this.mrTexture?.destroy();
    this.accumBuffer?.destroy();
    this.paramsBuffer.destroy();
    this.presentParamsBuffer.destroy();
  }

  get canvasEl(): HTMLCanvasElement {
    return this.canvas;
  }

  resize(widthPx: number, heightPx: number): void {
    if (widthPx === this.renderWidth && heightPx === this.renderHeight) return;
    this.renderWidth = Math.max(1, Math.floor(widthPx));
    this.renderHeight = Math.max(1, Math.floor(heightPx));
    this.canvas.width = this.renderWidth;
    this.canvas.height = this.renderHeight;

    this.accumBuffer?.destroy();
    const pixelCount = this.renderWidth * this.renderHeight;
    this.accumBuffer = this.device.createBuffer({
      size: pixelCount * 16, // vec4<f32>
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'accumulation',
    });

    this.invalidate();
    this.rebuildBindGroups();
  }

  invalidate(): void {
    this.frameIndex = 0;
    this.accumulationResetPending = true;
  }

  /** Upload scene geometry + materials + BVH. Invalidates accumulation. */
  updateScene(scene: ExtractedScene): void {
    this.sceneVersionKey = (this.sceneVersionKey + 1) >>> 0;
    this.sceneReady = false;

    const bvh = buildBvh(scene.indices, scene.positions);

    // Reorder indices + triMatIds so BVH leaf (triStart, triCount) references
    // contiguous triangles.
    const triCount = scene.indices.length / 3;
    const reorderedIndices = new Uint32Array(triCount * 3);
    const reorderedMatIds = new Uint32Array(triCount);
    for (let i = 0; i < triCount; i++) {
      const src = bvh.triRemap[i];
      reorderedIndices[i * 3 + 0] = scene.indices[src * 3 + 0];
      reorderedIndices[i * 3 + 1] = scene.indices[src * 3 + 1];
      reorderedIndices[i * 3 + 2] = scene.indices[src * 3 + 2];
      reorderedMatIds[i] = scene.triMaterialIds[src];
    }

    // Interleave vertex data (pos + normal + uv0 + uv1). `scene.uvs` is
    // packed as 4 floats/vertex; see sceneExtract for the TEXCOORD_0 /
    // TEXCOORD_1 rationale.
    const vertexCount = scene.positions.length / 3;
    const vertexData = new Float32Array(vertexCount * VERTEX_STRIDE_FLOATS);
    for (let i = 0; i < vertexCount; i++) {
      const base = i * VERTEX_STRIDE_FLOATS;
      vertexData[base + 0] = scene.positions[i * 3 + 0];
      vertexData[base + 1] = scene.positions[i * 3 + 1];
      vertexData[base + 2] = scene.positions[i * 3 + 2];
      vertexData[base + 3] = scene.normals[i * 3 + 0];
      vertexData[base + 4] = scene.normals[i * 3 + 1];
      vertexData[base + 5] = scene.normals[i * 3 + 2];
      vertexData[base + 6] = scene.uvs[i * 4 + 0] ?? 0;
      vertexData[base + 7] = scene.uvs[i * 4 + 1] ?? 0;
      vertexData[base + 8] = scene.uvs[i * 4 + 2] ?? 0;
      vertexData[base + 9] = scene.uvs[i * 4 + 3] ?? 0;
    }

    const materialsBytes = this.packMaterials(scene.materials);

    this.uploadMaterialTextures(scene);

    this.nodesBuffer?.destroy();
    this.indicesBuffer?.destroy();
    this.vertexBuffer?.destroy();
    this.triMatIdsBuffer?.destroy();
    this.materialsBuffer?.destroy();

    this.nodesBuffer = this.device.createBuffer({
      size: Math.max(32, bvh.nodes.byteLength),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'bvh-nodes',
    });
    this.device.queue.writeBuffer(this.nodesBuffer, 0, asBufferSource(bvh.nodes));

    this.indicesBuffer = this.device.createBuffer({
      size: Math.max(16, reorderedIndices.byteLength),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'indices',
    });
    this.device.queue.writeBuffer(this.indicesBuffer, 0, asBufferSource(reorderedIndices));

    this.vertexBuffer = this.device.createBuffer({
      size: Math.max(16, vertexData.byteLength),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'vertexData',
    });
    this.device.queue.writeBuffer(this.vertexBuffer, 0, asBufferSource(vertexData));

    this.triMatIdsBuffer = this.device.createBuffer({
      size: Math.max(16, reorderedMatIds.byteLength),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'triMatIds',
    });
    this.device.queue.writeBuffer(this.triMatIdsBuffer, 0, asBufferSource(reorderedMatIds));

    this.materialsBuffer = this.device.createBuffer({
      size: Math.max(32, materialsBytes.byteLength),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'materials',
    });
    this.device.queue.writeBuffer(this.materialsBuffer, 0, asBufferSource(materialsBytes));

    this.nodeCount = bvh.nodeCount;
    this.sceneReady = triCount > 0;
    this.rebuildBindGroups();
    this.invalidate();
  }

  /**
   * Upload lamp data and the corresponding IES candela texture array.
   * `grids` maps iesKey -> IesGrid; each lamp references one key.
   *
   * @param blurSigmaDeg Optional angular Gaussian blur sigma (in degrees)
   *                     applied to each IES layer before upload. Useful to
   *                     soften razor-sharp cone boundaries in the raw IES
   *                     data without changing the overall emission profile.
   */
  updateLamps(lamps: LampPathtracerInput[], grids: Map<string, IesGrid>, blurSigmaDeg = 0): void {
    this.lampsReady = false;

    const { ies, lamps: packedLamps } = packIesData(lamps, grids, this.iesAzSize, this.iesElSize, blurSigmaDeg);

    // Anchor exposure=0 to a "reasonable midtone" regardless of the absolute
    // candela magnitude in the IES file. Scene geometry/scale isn't factored
    // in (we have no cheap proxy for it), so this is a pragmatic approximation
    // rather than a true auto-exposure: a typical 1000 cd peak maps to ~ -2.5
    // EV of internal attenuation, which matches what manual tuning converged
    // on. Users can still tweak around this anchor via the exposure slider.
    this.autoExposureOffsetEv = ies.maxCandela > 0 ? Math.log2(AUTO_EXPOSURE_REFERENCE_CD / ies.maxCandela) : 0;
    const lampBytes = packLampBuffer(packedLamps);

    this.lampsBuffer?.destroy();
    this.lampsBuffer = this.device.createBuffer({
      size: Math.max(32, lampBytes.byteLength),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      label: 'lamps',
    });
    this.device.queue.writeBuffer(this.lampsBuffer, 0, asBufferSource(lampBytes));
    this.lampCount = packedLamps.length;

    // IES texture array: r32float, width=azSize, height=elSize, depth=layerCount
    this.iesTexture?.destroy();
    this.iesAzSize = ies.azSize;
    this.iesElSize = ies.elSize;
    this.iesLayerCount = ies.layerCount;
    this.iesTexture = this.device.createTexture({
      size: { width: ies.azSize, height: ies.elSize, depthOrArrayLayers: ies.layerCount },
      format: 'r32float',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      dimension: '2d',
      label: 'ies-array',
    });
    this.uploadIesLayers(this.iesTexture, ies);

    this.lampsReady = true;
    this.rebuildBindGroups();
    this.invalidate();
  }

  private uploadIesLayers(texture: GPUTexture, ies: PackedIesData): void {
    const stride = ies.azSize * ies.elSize;
    const bytesPerRow = ies.azSize * 4; // r32float = 4 bytes
    for (let layer = 0; layer < ies.layerCount; layer++) {
      const slice = ies.candelaLayers.subarray(layer * stride, (layer + 1) * stride);
      this.device.queue.writeTexture(
        { texture, origin: { x: 0, y: 0, z: layer } },
        asBufferSource(slice),
        { bytesPerRow, rowsPerImage: ies.elSize },
        { width: ies.azSize, height: ies.elSize, depthOrArrayLayers: 1 },
      );
    }
  }

  private packMaterials(mats: SceneMaterial[]): Float32Array {
    const out = new Float32Array(Math.max(1, mats.length) * MATERIAL_STRIDE_FLOATS);
    // Offsets 8-11 of each material are two i32 texture layer indices plus
    // two u32 pads. Alias via an Int32Array view so we can write the -1
    // sentinel correctly (Float32Array wouldn't).
    const i32 = new Int32Array(out.buffer);
    for (let i = 0; i < mats.length; i++) {
      const base = i * MATERIAL_STRIDE_FLOATS;
      const m = mats[i];
      out[base + 0] = m.baseColor[0];
      out[base + 1] = m.baseColor[1];
      out[base + 2] = m.baseColor[2];
      out[base + 3] = m.metallic;
      out[base + 4] = m.emissive[0];
      out[base + 5] = m.emissive[1];
      out[base + 6] = m.emissive[2];
      out[base + 7] = m.roughness;
      i32[base + 8] = m.baseColorLayer;
      i32[base + 9] = m.mrLayer;
      i32[base + 10] = 0;
      i32[base + 11] = 0;
      // baseColor UV transform row0 (m11, m12, m13, _)
      out[base + 12] = m.baseColorUvRow0[0];
      out[base + 13] = m.baseColorUvRow0[1];
      out[base + 14] = m.baseColorUvRow0[2];
      out[base + 15] = 0;
      // baseColor UV transform row1 (m21, m22, m23, _)
      out[base + 16] = m.baseColorUvRow1[0];
      out[base + 17] = m.baseColorUvRow1[1];
      out[base + 18] = m.baseColorUvRow1[2];
      out[base + 19] = 0;
      // mr UV transform row0
      out[base + 20] = m.mrUvRow0[0];
      out[base + 21] = m.mrUvRow0[1];
      out[base + 22] = m.mrUvRow0[2];
      out[base + 23] = 0;
      // mr UV transform row1
      out[base + 24] = m.mrUvRow1[0];
      out[base + 25] = m.mrUvRow1[1];
      out[base + 26] = m.mrUvRow1[2];
      out[base + 27] = 0;
      // UV channel selectors (u32). 0 = TEXCOORD_0 (mesh `attributes.uv`),
      // 1 = TEXCOORD_1 (mesh `attributes.uv1`). The shader uses these to
      // pick the right vertex UV set per texture, matching glTF's per-texture
      // `texCoord` field (three.js stores it on `Texture.channel`).
      i32[base + 28] = m.baseColorUvChannel;
      i32[base + 29] = m.mrUvChannel;
      i32[base + 30] = 0;
      i32[base + 31] = 0;
    }
    return out;
  }

  /**
   * (Re)create baseColor / metallicRoughness texture arrays and upload all
   * layers, including the full mip chain per layer. Without mipmaps +
   * per-hit LOD the path tracer aliases severely on tiled high-frequency
   * textures (the mean of that aliasing is a solid color, the "banding"
   * regime is a moire pattern) - which is exactly what you see if you bind
   * only mip 0 and always sample `textureSampleLevel(..., 0.0)`.
   *
   * Uploads go through `copyExternalImageToTexture` which takes an ImageBitmap
   * directly. That's faster than `writeTexture` + raw bytes and sidesteps the
   * "CORS-tainted canvas" SecurityError that `getImageData` sometimes hits.
   */
  private uploadMaterialTextures(scene: ExtractedScene): void {
    const canonicalSize = scene.textureSize;
    const mipLevelCount = Math.max(1, Math.floor(Math.log2(canonicalSize)) + 1);
    this.materialTexSize = canonicalSize;

    // Base color (sRGB-encoded source -> hardware-decoded on sample).
    this.baseColorTexture?.destroy();
    if (scene.baseColorTextures.length > 0) {
      this.baseColorTexture = this.device.createTexture({
        size: {
          width: canonicalSize,
          height: canonicalSize,
          depthOrArrayLayers: scene.baseColorTextures.length,
        },
        format: 'rgba8unorm-srgb',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT,
        mipLevelCount,
        dimension: '2d',
        label: 'basecolor-array',
      });
      for (let i = 0; i < scene.baseColorTextures.length; i++) {
        const layer = scene.baseColorTextures[i];
        for (let mip = 0; mip < layer.bitmaps.length && mip < mipLevelCount; mip++) {
          const bmp = layer.bitmaps[mip];
          this.device.queue.copyExternalImageToTexture(
            { source: bmp, flipY: false },
            {
              texture: this.baseColorTexture,
              origin: { x: 0, y: 0, z: i },
              mipLevel: mip,
            },
            { width: bmp.width, height: bmp.height, depthOrArrayLayers: 1 },
          );
        }
      }
    } else {
      this.baseColorTexture = this.createDummyTextureArray('rgba8unorm-srgb', 'dummy-basecolor');
    }

    // Metallic-roughness (linear data per glTF).
    this.mrTexture?.destroy();
    if (scene.metallicRoughnessTextures.length > 0) {
      this.mrTexture = this.device.createTexture({
        size: {
          width: canonicalSize,
          height: canonicalSize,
          depthOrArrayLayers: scene.metallicRoughnessTextures.length,
        },
        format: 'rgba8unorm',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT,
        mipLevelCount,
        dimension: '2d',
        label: 'mr-array',
      });
      for (let i = 0; i < scene.metallicRoughnessTextures.length; i++) {
        const layer = scene.metallicRoughnessTextures[i];
        for (let mip = 0; mip < layer.bitmaps.length && mip < mipLevelCount; mip++) {
          const bmp = layer.bitmaps[mip];
          this.device.queue.copyExternalImageToTexture(
            { source: bmp, flipY: false },
            {
              texture: this.mrTexture,
              origin: { x: 0, y: 0, z: i },
              mipLevel: mip,
            },
            { width: bmp.width, height: bmp.height, depthOrArrayLayers: 1 },
          );
        }
      }
    } else {
      this.mrTexture = this.createDummyTextureArray('rgba8unorm', 'dummy-mr');
    }

    console.info('[pathtracer] material textures uploaded', {
      baseColorLayers: scene.baseColorTextures.length,
      mrLayers: scene.metallicRoughnessTextures.length,
      textureSize: canonicalSize,
      mipLevelCount,
    });
  }

  private rebuildBindGroups(): void {
    // Present bind group only needs the accumulation buffer, so we can always
    // render an opaque canvas even before the scene + lamps are uploaded.
    if (this.presentBindGroupLayout && this.accumBuffer) {
      this.presentBindGroup = this.device.createBindGroup({
        layout: this.presentBindGroupLayout,
        entries: [
          { binding: 0, resource: { buffer: this.presentParamsBuffer } },
          { binding: 1, resource: { buffer: this.accumBuffer } },
        ],
      });
    } else {
      this.presentBindGroup = null;
    }

    if (
      !this.ptBindGroupLayout ||
      !this.nodesBuffer ||
      !this.indicesBuffer ||
      !this.vertexBuffer ||
      !this.triMatIdsBuffer ||
      !this.materialsBuffer ||
      !this.lampsBuffer ||
      !this.iesTexture ||
      !this.baseColorTexture ||
      !this.mrTexture ||
      !this.accumBuffer
    ) {
      this.ptBindGroup = null;
      return;
    }

    this.ptBindGroup = this.device.createBindGroup({
      layout: this.ptBindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: this.paramsBuffer } },
        { binding: 1, resource: { buffer: this.nodesBuffer } },
        { binding: 2, resource: { buffer: this.indicesBuffer } },
        { binding: 3, resource: { buffer: this.vertexBuffer } },
        { binding: 4, resource: { buffer: this.triMatIdsBuffer } },
        { binding: 5, resource: { buffer: this.materialsBuffer } },
        { binding: 6, resource: { buffer: this.lampsBuffer } },
        {
          binding: 7,
          resource: this.iesTexture.createView({
            dimension: '2d-array',
            arrayLayerCount: this.iesLayerCount,
          }),
        },
        { binding: 8, resource: { buffer: this.accumBuffer } },
        {
          binding: 9,
          resource: this.baseColorTexture.createView({ dimension: '2d-array' }),
        },
        {
          binding: 10,
          resource: this.mrTexture.createView({ dimension: '2d-array' }),
        },
        { binding: 11, resource: this.texSampler },
      ],
    });
  }

  private writeParamsUbo(camera: CameraSnapshot, reset: boolean): void {
    const buf = new ArrayBuffer(PARAMS_UBO_SIZE);
    const f32 = new Float32Array(buf);
    const u32 = new Uint32Array(buf);

    // invViewProj = inverse(projection * matrixWorldInverse)
    const proj = camera.projectionMatrix;
    const invProj = new THREE.Matrix4().copy(proj).invert();
    const matWorld = new THREE.Matrix4().copy(camera.matrixWorldInverse).invert();
    // ray origin = camera position; ray via invViewProj applied to NDC.
    // invViewProj = matWorld * invProj
    const invViewProj = new THREE.Matrix4().multiplyMatrices(matWorld, invProj);
    const e = invViewProj.elements;
    // three.js stores column-major matching WGSL.
    for (let i = 0; i < 16; i++) f32[i] = e[i];

    f32[16] = camera.position.x;
    f32[17] = camera.position.y;
    f32[18] = camera.position.z;
    // f32[19] is pad

    u32[20] = this.renderWidth;
    u32[21] = this.renderHeight;
    u32[22] = this.frameIndex;
    u32[23] = reset ? 1 : 0;
    u32[24] = this.lampCount;
    u32[25] = this.maxBounces;
    u32[26] = this.iesAzSize;
    u32[27] = this.iesElSize;
    u32[28] = this.nodeCount;
    // Vertical pixel cone half-angle, in radians. Derived from the projection
    // matrix's Y scale (proj[1][1] = 1 / tan(fov/2)) and the framebuffer
    // height: full pixel angle ~ 2 / (proj[1][1] * height). Used by the
    // shader to compute anisotropic LOD per hit so textures don't alias.
    const projYScale = camera.projectionMatrix.elements[5];
    const pixelAngle = this.renderHeight > 0 && projYScale > 0 ? 2.0 / (projYScale * this.renderHeight) : 0.0025;
    f32[29] = pixelAngle;
    // Canonical texture-array side length, passed to the shader so it can
    // convert "texels of footprint" -> mip level (log2).
    f32[30] = this.materialTexSize;
    // User-controllable texture LOD bias (mip levels), folded into the shader's
    // clamp(log2(texelFootprint) + bias, 0, 10) when sampling material textures.
    f32[31] = this.lodBias;

    f32[32] = this.lightTint[0];
    f32[33] = this.lightTint[1];
    f32[34] = this.lightTint[2];
    // Scalar ambient fill + dedicated tint (separate from lightTint).
    f32[35] = this.ambient;
    f32[36] = this.ambientTint[0];
    f32[37] = this.ambientTint[1];
    f32[38] = this.ambientTint[2];
    f32[39] = 0; // pad to 16-byte alignment for WGSL uniform layout

    this.device.queue.writeBuffer(this.paramsBuffer, 0, buf);
  }

  private writePresentUbo(exposure: number): void {
    const buf = new ArrayBuffer(PRESENT_UBO_SIZE);
    const u32 = new Uint32Array(buf);
    const f32 = new Float32Array(buf);
    u32[0] = this.renderWidth;
    u32[1] = this.renderHeight;
    // Fold the auto-exposure anchor into the final EV so the UI slider is
    // a simple offset around a "good default" midtone.
    f32[2] = exposure + this.autoExposureOffsetEv;
    u32[3] = 0;
    this.device.queue.writeBuffer(this.presentParamsBuffer, 0, buf);
  }

  /**
   * Scalar ambient fill added on ray miss (primary + indirect). Chromaticity
   * comes from `ambientTint`, not `lightTint`, so ambient can be configured
   * independently from the lamp color-temperature control.
   * Invalidates accumulation because the rendered image changes.
   */
  setAmbient(value: number): void {
    if (this.ambient === value) return;
    this.ambient = value;
    this.invalidate();
  }

  /**
   * Set the ambient fill chromaticity (linear sRGB), independent of lamp tint.
   * Invalidates accumulation since ray-miss lighting changes.
   */
  setAmbientTint(rgb: readonly [number, number, number]): void {
    if (this.ambientTint[0] === rgb[0] && this.ambientTint[1] === rgb[1] && this.ambientTint[2] === rgb[2]) {
      return;
    }
    this.ambientTint = [rgb[0], rgb[1], rgb[2]];
    this.invalidate();
  }

  /**
   * Max path bounces (indirect + NEE). Lower values speed up the compute pass; higher
   * recovers more interreflection. Typically 2–3 is a good real-time default.
   */
  setMaxBounces(n: number): void {
    const clamped = Math.max(2, Math.min(8, Math.floor(n)));
    if (this.maxBounces === clamped) return;
    this.maxBounces = clamped;
    this.invalidate();
  }

  /**
   * Set the luminance-normalized light tint (linear sRGB). Invalidates
   * accumulation since the rendered image depends on this color.
   */
  setLightTint(rgb: readonly [number, number, number]): void {
    if (this.lightTint[0] === rgb[0] && this.lightTint[1] === rgb[1] && this.lightTint[2] === rgb[2]) {
      return;
    }
    this.lightTint = [rgb[0], rgb[1], rgb[2]];
    this.invalidate();
  }

  /**
   * Render one sample (if under sample cap) and present. Call once per rAF.
   *
   * @param camera        Camera snapshot from R3F (projection + view matrices + position in world space).
   * @param forceReset    Set true to reset accumulation (e.g. after exposure change? no - exposure doesn't affect accumulation).
   * @param options       Exposure + max sample cap.
   */
  render(camera: CameraSnapshot, forceReset: boolean, options: RenderOptions): void {
    if (!this.ptPipeline || !this.presentPipeline) return;
    if (this.renderWidth === 0 || this.renderHeight === 0) return;
    // Always present if we have a present bind group, so the WebGPU canvas
    // stays opaque (doesn't leak the WebGL canvas/grid through when blank).
    if (!this.ptBindGroup || !this.sceneReady || !this.lampsReady) {
      this.writePresentUbo(options.exposure);
      const enc = this.device.createCommandEncoder();
      this.appendPresentRenderPass(enc);
      this.device.queue.submit([enc.finish()]);
      return;
    }

    const key = pathTracerCameraKey(camera);
    let reset = forceReset;
    if (key !== this.lastCameraKey) {
      reset = true;
      this.lastCameraKey = key;
    }
    if (this.accumulationResetPending) {
      reset = true;
      this.accumulationResetPending = false;
    }
    if (reset) this.frameIndex = 0;

    const doAccumulate = this.frameIndex < options.maxSamples;

    if (doAccumulate) {
      this.writeParamsUbo(camera, reset);
    }
    this.writePresentUbo(options.exposure);

    const encoder = this.device.createCommandEncoder();
    if (doAccumulate) {
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.ptPipeline!);
      pass.setBindGroup(0, this.ptBindGroup!);
      pass.dispatchWorkgroups(Math.ceil(this.renderWidth / 8), Math.ceil(this.renderHeight / 8));
      pass.end();
      this.frameIndex += 1;
    }
    this.appendPresentRenderPass(encoder);
    this.device.queue.submit([encoder.finish()]);
  }

  /** Appends the fullscreen present pass. Caller must have called `writePresentUbo` if exposure changed. */
  private appendPresentRenderPass(encoder: GPUCommandEncoder): void {
    if (!this.presentPipeline || !this.presentBindGroup) return;
    if (this.renderWidth === 0 || this.renderHeight === 0) return;
    const target = this.context.getCurrentTexture().createView();
    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: target,
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          loadOp: 'clear',
          storeOp: 'store',
        },
      ],
    });
    pass.setPipeline(this.presentPipeline);
    pass.setBindGroup(0, this.presentBindGroup);
    pass.draw(3);
    pass.end();
  }

  /** Current accumulated sample count. */
  get currentSampleCount(): number {
    return this.frameIndex;
  }
}
